import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { Platform } from 'react-native';
import { showError, showInfo, showSuccess } from '../../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { reportUserVisibleFailure } from '../../../shared/sentry/sentryUser';
import { catalogItemNoun } from '../../domain/catalogTerminology';
import { getErrorMessage } from '../../../shared/errorMessage';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import { useEmployeesStore, withSelfOwner } from '../../../team/store/useEmployeesStore';
import { useBranchBarcodeInputModeForCurrentBranch } from '../../../shell/session/hooks/useBranchBarcodeInputMode';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { createBarcodeScanDedupe, looksLikeBarcodeHardwareSubmit } from '../../../shared/product-search/productBarcodeInput'
import { effectiveProductSearchQuery } from '../../../shared/product-search/productSearchUx';
import { parsePercentInput } from '../../../shared/numeric/localeNumeric'
import { bulkCoefficientFromPercent } from '../../../shared/numeric/public-api';
import { subscribeCatalogSynced } from '../../../sync/core/catalogSyncListeners';
import type { OperationalConfirmModalProps } from '../../../shared/modals/OperationalConfirmModal';
import { barcodeSearchFieldValue } from '../../domain/barcodeScanField';
import { buildProductDraftFromSuggestion, lookupProductByBarcode } from '../../infra/productCatalogSearch';
import {
  DEFAULT_BULK_PRICE_ROUNDING,
  type BulkPriceRounding,
} from '../../application/bulkPriceRounding';
import {
  bulkCategoryScopeFromCatalogFilter,
  DEFAULT_BULK_CATEGORY_SCOPE,
  type BulkCategoryScope,
} from '../../application/bulkCategoryScope';
import { isBulkPreviewScopeAllowed } from '../../application/bulkPreviewScope';
import {
  normalizeProductCode,
  bulkUpdatePricesByCoefficient,
  getBulkApplyProductIds,
  getBulkPreview,
  getBulkScopeCounts,
  deactivateProduct,
  reactivateProduct,
} from '../../infra/productsRepo';
import { listCategories } from '../../infra/categoriesRepo';
import { getProductCatalogHistory, getProductCatalogHistoryForExport } from '../../infra/productCatalogEventQueries';
import { buildProductCatalogHistoryCsv } from '../../infra/productCatalogHistoryCsv';
import { shareOrDownloadCsv } from '../../../shared/csv-core/infra/shareCsv';
import { createCategoryDisplayResolver, formatProductCatalogEventSummary } from '../../domain/catalogEventSummary';
import { getProductCatalogEventTypeLabel } from '../../domain/catalogLabels';
import { productMatchesCategoryFilter } from '../../domain/categoryTypes';
import type { CategoryRecord } from '../../domain/categoryTypes';
import { BULK_COEFFICIENT_PREVIEW_FETCH_LIMIT } from '../../application/bulkCoefficientConstants';
import type { CatalogFilter, CatalogProductRow, ProductCatalogEventRow, ProductCatalogHistoryFilters, BulkPreviewItem } from '../../domain/catalogTypes';
import type { BulkScopeCounts } from '../../infra/productCatalogBulk';
import type { Product, CatalogItemTypeFilter } from '../../domain/productTypes';
import type { ProductDraft } from '../../domain/productDraft';
import type { ProductLookupResult } from '../../domain/barcodeLookup';
import { CATALOG_HISTORY_PAGE_SIZE } from '../../domain/catalogPaginationConstants';
import {
  CATALOG_DEACTIVATE_STOCK_BLOCK_MESSAGE,
  CATALOG_DEACTIVATE_STOCK_CHECKING_MESSAGE,
  CATALOG_DEACTIVATE_STOCK_UNVERIFIED_MESSAGE,
  catalogDeactivateBlock,
  type CatalogProductConfirmKind,
  type CatalogStockCheckResult,
} from '../../domain/catalogProductConfirm';

type RemoteHit = Extract<ProductLookupResult, { kind: 'remote_hit' }>;

// ─── Barcode helpers ────────────────────────────────────────────────────────

function formatLookupError(error: unknown, fallback: string): string {
  const message = getErrorMessage(error, fallback);
  if (/iniciar sesión|sesión inválida/i.test(message)) return 'Necesitás iniciar sesión para buscar datos remotos.';
  if (/internet|network|conexión/i.test(message)) return 'No hay conexión para consultar datos remotos.';
  if (/código de barras inválido/i.test(message)) return 'El código leído no es válido.';
  return message;
}

// ─── Confirm flow types ─────────────────────────────────────────────────────

export type { CatalogProductConfirmKind } from '../../domain/catalogProductConfirm';

type PendingConfirm = {
  kind: CatalogProductConfirmKind;
  product: CatalogProductRow;
};

const CONFIRM_COPY: Record<
  CatalogProductConfirmKind,
  {
    title: string;
    message: string;
    confirmLabel: string;
    destructive: boolean;
    iconName: NonNullable<OperationalConfirmModalProps['iconName']>;
  }
> = {
  deactivate: {
    title: '¿Desactivar este ítem?',
    message: 'Deja de aparecer en el mostrador. Lo podés volver a mostrar después.',
    confirmLabel: 'Desactivar',
    destructive: true,
    iconName: 'eye-off-outline',
  },
  duplicate: {
    title: 'Duplicar ítem',
    message: 'Copiamos todo menos el código de barras y el SKU. El nombre queda con “(copia)” — lo podés cambiar antes de guardar.',
    confirmLabel: 'Continuar',
    destructive: false,
    iconName: 'copy-outline',
  },
  reactivate: {
    title: 'Activar ítem',
    message: 'Vuelve a aparecer en el catálogo y en el mostrador.',
    confirmLabel: 'Activar',
    destructive: false,
    iconName: 'eye-outline',
  },
};

// ─── Bulk price defaults ────────────────────────────────────────────────────

const bulkPriceDefaults = () => ({
  bulkPriceVisible: false,
  bulkPriceCategoryScope: DEFAULT_BULK_CATEGORY_SCOPE,
  bulkPriceSearch: '',
  bulkPricePercentInput: '',
  bulkPricePreview: [] as BulkPreviewItem[],
  bulkPriceApplyTotal: 0,
  bulkPricePreviewLoading: false,
  bulkPriceSaving: false,
  bulkPriceSaveProgress: null as { done: number; total: number } | null,
  bulkPriceScannerVisible: false,
  bulkPriceRounding: DEFAULT_BULK_PRICE_ROUNDING,
});

// ─── Main view model ────────────────────────────────────────────────────────

type Params = {
  canEdit: boolean;
  user: { id: string; email?: string | null } | null;
  currentBranchId: string | null | undefined;
  loadFirstPage: () => Promise<void>;
  // filter state (for barcode matching)
  statusFilter: CatalogFilter;
  categoryFilter: Product['categoryId'] | 'all';
  itemTypeFilter: CatalogItemTypeFilter;
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
  setStatusFilter: (v: CatalogFilter) => void;
  setCategoryFilter: (v: Product['categoryId'] | 'all') => void;
  setItemTypeFilter: (v: CatalogItemTypeFilter) => void;
  // overlay setters
  setSelectedProduct: (p: CatalogProductRow | null) => void;
  setSearchScannerVisible: (v: boolean) => void;
  setFormScannerVisible: (v: boolean) => void;
  setLookupFeedback: (s: string | null) => void;
  setRemoteSuggestion: (r: RemoteHit | null) => void;
  setFormSuggestion: (r: RemoteHit | null) => void;
  setDraft: (d: ProductDraft | ((prev: ProductDraft) => ProductDraft)) => void;
  editingProduct: CatalogProductRow | null;
  draft: ProductDraft;
  clearCatalogSearchPin: () => void;
  // from form VM
  openDuplicateModal: (product: CatalogProductRow) => void | Promise<void>;
  /**
   * Estado de lectura de stock del producto para el confirm de desactivar.
   * `retry` relanza la lectura cuando falló o hay que reverificar.
   */
  getProductStockCheck: (product: CatalogProductRow) => {
    check: CatalogStockCheckResult;
    retry: () => void;
  };
};

export function useCatalogActionsVm({
  canEdit,
  user,
  currentBranchId,
  loadFirstPage,
  statusFilter,
  categoryFilter,
  itemTypeFilter,
  searchQuery,
  setSearchQuery,
  setStatusFilter,
  setCategoryFilter,
  setItemTypeFilter,
  setSelectedProduct,
  setSearchScannerVisible,
  setFormScannerVisible,
  setLookupFeedback,
  setRemoteSuggestion,
  setFormSuggestion,
  setDraft,
  editingProduct,
  draft,
  clearCatalogSearchPin,
  openDuplicateModal,
  getProductStockCheck,
}: Params) {
  // ── Barcode ──
  const barcodeScanEnabled = useHasBusinessCapability('barcode_scan');
  const {
    allowCamera: branchAllowsCamera,
    allowHardwareWedge: branchAllowsHardware,
  } = useBranchBarcodeInputModeForCurrentBranch();
  const barcodeAllowCamera = barcodeScanEnabled && branchAllowsCamera;
  const barcodeAllowHardware = barcodeScanEnabled && branchAllowsHardware;
  const catalogSearchDedupe = useMemo(() => createBarcodeScanDedupe(), []);
  const catalogFormBarcodeDedupe = useMemo(() => createBarcodeScanDedupe(), []);

  const productMatchesCurrentFilters = useCallback(
    (product: CatalogProductRow) => {
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' ? product.isActive : !product.isActive);
      const matchesCategory = productMatchesCategoryFilter(product.categoryId, categoryFilter);
      const matchesItemType = itemTypeFilter === 'all' || product.itemType === itemTypeFilter;
      return matchesStatus && matchesCategory && matchesItemType;
    },
    [statusFilter, categoryFilter, itemTypeFilter],
  );

  const handleSearchScan = useCallback(
    async (rawCode: string) => {
      setSearchScannerVisible(false);
      clearCatalogSearchPin?.();
      const normalizedCode = normalizeProductCode(rawCode);
      setSearchQuery(normalizedCode);
      try {
        const result = await lookupProductByBarcode({ code: rawCode, includeInactive: true, allowRemoteLookup: true });
        setSearchQuery(result.barcode);
        if (result.kind === 'local_hit') {
          setRemoteSuggestion(null);
          if (result.product.isMisc) {
            setSearchQuery('');
            setLookupFeedback('Varios es interno del mostrador. No se carga desde Catálogo.');
            return;
          }
          if (!productMatchesCurrentFilters(result.product)) {
            setStatusFilter(result.product.isActive ? 'active' : 'inactive');
            setCategoryFilter('all');
            setItemTypeFilter('all');
            setLookupFeedback(`Encontramos: ${result.product.name}. Ajustamos filtros para mostrártelo.`);
          } else {
            setLookupFeedback(`Encontramos: ${result.product.name}.`);
          }
          // FIND: conserva el código para filtrar el listado; el campo selecciona todo para el próximo HID.
          return;
        }
        if (result.kind === 'remote_hit') {
          setRemoteSuggestion(result);
          setLookupFeedback(null);
          return;
        }
        setRemoteSuggestion(null);
        if (result.kind === 'offline_miss') {
          setLookupFeedback('No está cargado y no hay internet para buscarlo.');
        } else {
          setLookupFeedback('Ese código no está cargado.');
        }
      } catch (error: unknown) {
        setRemoteSuggestion(null);
        setLookupFeedback(formatLookupError(error, 'Leímos el código, pero no pudimos buscar datos.'));
      }
    },
    [
      setSearchScannerVisible, setSearchQuery, setRemoteSuggestion, setLookupFeedback,
      setStatusFilter, setCategoryFilter, setItemTypeFilter,
      productMatchesCurrentFilters, clearCatalogSearchPin,
    ],
  );

  const handleFormScan = useCallback(
    async (rawCode: string) => {
      setFormScannerVisible(false);
      const normalizedCode = normalizeProductCode(rawCode);
      setDraft((current) => {
        const next = { ...current, barcode: normalizedCode };
        if (!current.sku.trim()) return { ...next, sku: normalizedCode };
        return next;
      });
      try {
        const result = await lookupProductByBarcode({ code: rawCode, includeInactive: true, allowRemoteLookup: true });
        setDraft((current) => {
          const next = { ...current, barcode: result.barcode };
          if (!current.sku.trim()) return { ...next, sku: result.barcode };
          return next;
        });
        if (result.kind === 'local_hit') {
          setFormSuggestion(null);
          if (result.product.isMisc) {
            setDraft((current) => ({ ...current, sku: '', barcode: '' }));
            showInfo('Línea interna', 'Varios es interno del mostrador. No se edita ni se usa como código.');
            return;
          }
          if (result.product.id !== editingProduct?.id) {
            showError('Ese código ya existe', `Ese código ya lo usa "${result.product.name}".`);
          }
          return;
        }
        if (result.kind === 'remote_hit') {
          setFormSuggestion(result);
          setDraft((current) => ({
            ...current,
            ...buildProductDraftFromSuggestion({ barcode: result.barcode, suggestion: result.suggestion }),
            price: current.price,
          }));
          return;
        }
        setFormSuggestion(null);
        if (result.kind === 'offline_miss') {
          showInfo('Sin internet', 'Guardamos el código, pero no pudimos buscar datos en internet.');
        } else {
          showInfo('No encontramos datos', 'No encontramos ese código.');
        }
      } catch (error: unknown) {
        setFormSuggestion(null);
        showError('No pudimos buscar datos', formatLookupError(error, 'Guardamos el código, pero ahora no pudimos revisar los datos.'));
      }
    },
    [setFormScannerVisible, setDraft, setFormSuggestion, editingProduct],
  );

  useEffect(() => {
    if (!barcodeAllowCamera) {
      setSearchScannerVisible(false);
      setFormScannerVisible(false);
    }
  }, [barcodeAllowCamera, setSearchScannerVisible, setFormScannerVisible]);

  const handleSearchHardwareSubmit = useCallback(() => {
    if (!barcodeAllowHardware) return;
    const q = searchQuery.trim();
    if (!looksLikeBarcodeHardwareSubmit(q)) return;
    if (!catalogSearchDedupe(q)) return;
    void handleSearchScan(q);
  }, [barcodeAllowHardware, searchQuery, catalogSearchDedupe, handleSearchScan]);

  const handleFormBarcodeHardwareSubmit = useCallback(() => {
    if (!barcodeAllowHardware) return;
    const q = draft.barcode.trim();
    if (!looksLikeBarcodeHardwareSubmit(q)) return;
    if (!catalogFormBarcodeDedupe(q)) return;
    void handleFormScan(q);
  }, [barcodeAllowHardware, draft.barcode, catalogFormBarcodeDedupe, handleFormScan]);

  // ── Confirm flow ──
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const requestConfirm = useCallback(
    (kind: CatalogProductConfirmKind, product: CatalogProductRow) => {
      if (!canEdit) {
        const msg =
          kind === 'duplicate' ? 'Podés ver productos, pero no duplicarlos.' :
          kind === 'deactivate' ? 'Podés ver productos, pero no desactivarlos.' :
          'Podés ver productos, pero no activarlos.';
        showError('No podés hacer cambios', msg);
        return;
      }
      if (kind === 'duplicate' && product.isMisc) {
        showInfo('Línea interna', 'Varios es interno del mostrador. No se duplica.');
        return;
      }
      setPendingConfirm({ kind, product });
    },
    [canEdit],
  );

  const handleDeactivate = useCallback(
    (product: CatalogProductRow) => requestConfirm('deactivate', product),
    [requestConfirm],
  );
  const handleRequestDuplicate = useCallback(
    (product: CatalogProductRow) => requestConfirm('duplicate', product),
    [requestConfirm],
  );
  const handleRequestReactivate = useCallback(
    (product: CatalogProductRow) => requestConfirm('reactivate', product),
    [requestConfirm],
  );

  const closeProductConfirm = useCallback(() => {
    if (confirmSubmitting) return;
    setPendingConfirm(null);
  }, [confirmSubmitting]);

  const confirmProductAction = useCallback(async () => {
    if (!pendingConfirm) return;
    const { kind, product } = pendingConfirm;
    if (kind === 'duplicate') {
      setPendingConfirm(null);
      setSelectedProduct(null);
      await openDuplicateModal(product);
      return;
    }
    if (kind === 'deactivate') {
      const probe = getProductStockCheck?.(product);
      const block = probe ? catalogDeactivateBlock(product.itemType, probe.check) : 'none';
      if (block === 'unverified') {
        // Reintentar la verificación: el confirm queda abierto y recalcula.
        probe?.retry();
        return;
      }
      if (block === 'checking') {
        return; // el botón queda deshabilitado hasta que termine la lectura.
      }
      if (block === 'stock') {
        setPendingConfirm(null);
        return;
      }
    }
    setConfirmSubmitting(true);
    try {
      if (kind === 'deactivate') {
        await deactivateProduct(product.id, { performedByUserId: user?.id ?? null });
        await loadFirstPage();
        setSelectedProduct(null);
        setPendingConfirm(null);
        const noun = catalogItemNoun(product.itemType);
        showSuccess(`${noun.Singular} desactivado`, `El ${noun.singular} quedó inactivo.`);
      } else {
        await reactivateProduct(product.id, { performedByUserId: user?.id ?? null });
        await loadFirstPage();
        setSelectedProduct(null);
        setPendingConfirm(null);
        const noun = catalogItemNoun(product.itemType);
        showSuccess(`${noun.Singular} activado`, `El ${noun.singular} vuelve a aparecer en la lista.`);
      }
    } catch (error: any) {
      const title = kind === 'deactivate' ? 'No pudimos desactivarlo' : 'No pudimos activarlo';
      presentUnexpectedFailure({ module: 'catalog', op: 'bulk-toggle', error, title, userMessage: 'Ocurrió un error' });
    } finally {
      setConfirmSubmitting(false);
    }
  }, [pendingConfirm, user?.id, loadFirstPage, setSelectedProduct, openDuplicateModal, getProductStockCheck]);

  const productConfirmModalProps = useMemo(() => {
    if (!pendingConfirm) return null;
    const copy = CONFIRM_COPY[pendingConfirm.kind];
    let message = copy.message;
    let stockState: 'none' | 'stock' | 'unverified' | 'checking' = 'none';
    if (pendingConfirm.kind === 'deactivate') {
      const product = pendingConfirm.product;
      const probe = getProductStockCheck?.(product);
      stockState = probe ? catalogDeactivateBlock(product.itemType, probe.check) : 'none';
      if (stockState === 'stock') {
        message = CATALOG_DEACTIVATE_STOCK_BLOCK_MESSAGE;
      } else if (stockState === 'unverified') {
        message = CATALOG_DEACTIVATE_STOCK_UNVERIFIED_MESSAGE;
      } else if (stockState === 'checking') {
        message = CATALOG_DEACTIVATE_STOCK_CHECKING_MESSAGE;
      }
    }
    const confirmLabel = confirmSubmitting
      ? pendingConfirm.kind === 'deactivate' ? 'Desactivando…' :
        pendingConfirm.kind === 'reactivate' ? 'Activando…' : 'Procesando…'
      : stockState === 'stock' ? 'Entendido'
      : stockState === 'unverified' ? 'Reintentar'
      : stockState === 'checking' ? 'Verificando…'
      : copy.confirmLabel;
    return {
      ...copy, message, confirmLabel,
      entityName: pendingConfirm.product.name,
      loading: confirmSubmitting,
      destructive: stockState === 'none' ? copy.destructive : false,
      confirmDisabled: stockState === 'checking' || confirmSubmitting,
    };
  }, [pendingConfirm, confirmSubmitting, getProductStockCheck]);

  // ── Bulk price ──
  const [bulkPriceVisible, setBulkPriceVisible] = useState(false);
  const [bulkPriceCategoryScope, setBulkPriceCategoryScope] = useState<BulkCategoryScope>(DEFAULT_BULK_CATEGORY_SCOPE);
  const [bulkPriceSearch, setBulkPriceSearch] = useState('');
  const [bulkPricePercentInput, setBulkPricePercentInput] = useState('');
  const [bulkPricePreview, setBulkPricePreview] = useState<BulkPreviewItem[]>([]);
  const [bulkPriceApplyTotal, setBulkPriceApplyTotal] = useState(0);
  const [bulkPricePreviewLoading, setBulkPricePreviewLoading] = useState(false);
  const [bulkPriceSaving, setBulkPriceSaving] = useState(false);
  const [bulkPriceSaveProgress, setBulkPriceSaveProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkPriceScannerVisible, setBulkPriceScannerVisible] = useState(false);
  const [bulkPriceScopeCounts, setBulkPriceScopeCounts] = useState<BulkScopeCounts | null>(null);
  const [bulkPriceRounding, setBulkPriceRounding] = useState<BulkPriceRounding>(DEFAULT_BULK_PRICE_ROUNDING);

  const resetBulkPrice = useCallback(() => {
    const defaults = bulkPriceDefaults();
    setBulkPriceVisible(defaults.bulkPriceVisible);
    setBulkPriceCategoryScope(defaults.bulkPriceCategoryScope);
    setBulkPriceSearch(defaults.bulkPriceSearch);
    setBulkPricePercentInput(defaults.bulkPricePercentInput);
    setBulkPricePreview(defaults.bulkPricePreview);
    setBulkPriceApplyTotal(defaults.bulkPriceApplyTotal);
    setBulkPricePreviewLoading(defaults.bulkPricePreviewLoading);
    setBulkPriceSaving(defaults.bulkPriceSaving);
    setBulkPriceSaveProgress(defaults.bulkPriceSaveProgress);
    setBulkPriceScannerVisible(defaults.bulkPriceScannerVisible);
    setBulkPriceRounding(defaults.bulkPriceRounding);
  }, []);

  const catalogBulkPriceDedupe = useMemo(() => createBarcodeScanDedupe(), []);

  // El bulk siempre abre en el paso 1 (configuración): porcentaje vacío, sin
  // vista previa persistida — confirmar un % viejo por accidente era un P0.
  const openBulkPriceModal = useCallback(() => {
    if (!canEdit) {
      showInfo('Modo solo lectura', 'Podés ver el catálogo. No podés cambiar precios.');
      return;
    }
    setBulkPricePreview([]);
    setBulkPricePercentInput('');
    setBulkPriceRounding(DEFAULT_BULK_PRICE_ROUNDING);
    setBulkPriceCategoryScope(bulkCategoryScopeFromCatalogFilter(categoryFilter));
    setBulkPriceApplyTotal(0);
    setBulkPriceSearch(searchQuery.trim());
    setBulkPriceVisible(true);
  }, [canEdit, categoryFilter, searchQuery]);

  // Conteo honesto del alcance (con precio / sin precio) — se recalcula al
  // cambiar categoría/búsqueda mientras el modal está abierto.
  useEffect(() => {
    if (!bulkPriceVisible) {
      setBulkPriceScopeCounts(null);
      return;
    }
    let alive = true;
    void getBulkScopeCounts({
      categoryScope: bulkPriceCategoryScope,
      search: effectiveProductSearchQuery(bulkPriceSearch.trim()),
    }).then((counts) => {
      if (alive) setBulkPriceScopeCounts(counts);
    }).catch(() => {
      if (alive) setBulkPriceScopeCounts(null);
    });
    return () => {
      alive = false;
    };
  }, [bulkPriceVisible, bulkPriceCategoryScope, bulkPriceSearch]);

  const loadBulkPricePreview = useCallback(async () => {
    const pctNum = parsePercentInput(bulkPricePercentInput);
    if (pctNum === null) {
      showError('Porcentaje inválido', 'Ingresá un número (ej. 5 para subir precios o -10 para bajarlos).');
      return;
    }
    if (!isBulkPreviewScopeAllowed(bulkPriceCategoryScope, bulkPriceSearch)) {
      showInfo('Alcance incompleto', 'Elegí al menos una categoría para incluir o excluir.');
      return;
    }
    setBulkPricePreviewLoading(true);
    try {
      const { items, applyTotal } = await getBulkPreview({
        categoryScope: bulkPriceCategoryScope,
        search: effectiveProductSearchQuery(bulkPriceSearch.trim()),
        branchId: currentBranchId ?? undefined,
        coefficient: bulkCoefficientFromPercent(bulkPricePercentInput),
        applyTo: 'prices',
        previewLimit: BULK_COEFFICIENT_PREVIEW_FETCH_LIMIT,
        rounding: bulkPriceRounding,
      });
      setBulkPricePreview(items);
      setBulkPriceApplyTotal(applyTotal);
      if (items.length === 0) {
        showInfo(
          'Sin resultados',
          bulkPriceScopeCounts && bulkPriceScopeCounts.unpriced > 0 && bulkPriceScopeCounts.priced === 0
            ? 'En este alcance ningún producto tiene precio cargado. El coeficiente solo toca productos con precio.'
            : 'No hay productos que coincidan con el filtro.',
        );
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'No se pudo cargar la vista previa.';
      showError('Error', message);
    } finally {
      setBulkPricePreviewLoading(false);
    }
  }, [bulkPriceCategoryScope, bulkPricePercentInput, bulkPriceRounding, bulkPriceSearch, bulkPriceScopeCounts, currentBranchId]);

  const applyBulkPriceUpdate = useCallback(async () => {
    if (bulkPricePreview.length === 0) return;
    if (parsePercentInput(bulkPricePercentInput) === null) return;
    setBulkPriceSaving(true);
    setBulkPriceSaveProgress(null);
    try {
      const productIds = await getBulkApplyProductIds({
        categoryScope: bulkPriceCategoryScope,
        search: effectiveProductSearchQuery(bulkPriceSearch.trim()),
        applyTo: 'prices',
      });
      if (productIds.length === 0) {
        showInfo('Sin resultados', 'No hay productos con precio en este alcance.');
        return;
      }
      const updated = await bulkUpdatePricesByCoefficient(
        productIds,
        bulkCoefficientFromPercent(bulkPricePercentInput),
        {
          performedByUserId: user?.id ?? null,
          rounding: bulkPriceRounding,
          categoryScope: bulkPriceCategoryScope,
          onProgress: (p) => setBulkPriceSaveProgress(p),
        },
      );
      showSuccess('Precios actualizados', `Se actualizaron ${updated} precios.`);
      setBulkPriceVisible(false);
      setBulkPricePreview([]);
      setBulkPriceApplyTotal(0);
      await loadFirstPage();
    } catch (e: unknown) {
      reportUserVisibleFailure({ module: 'catalog', op: 'catalog.bulk_price_apply', error: e, title: 'No se pudo aplicar la actualización.' });
      const message = e instanceof Error ? e.message : 'No se pudo aplicar la actualización.';
      showError('Error', message);
    } finally {
      setBulkPriceSaving(false);
      setBulkPriceSaveProgress(null);
    }
  }, [bulkPriceCategoryScope, bulkPricePercentInput, bulkPricePreview.length, bulkPriceRounding, bulkPriceSearch, loadFirstPage, user?.id]);

  const handleBulkPriceScan = useCallback(async (rawCode: string) => {
    setBulkPriceScannerVisible(false);
    try {
      const result = await lookupProductByBarcode({ code: rawCode, allowRemoteLookup: true });
      setBulkPriceSearch(barcodeSearchFieldValue(result, rawCode));
      if (result.kind === 'local_hit' && (result.product.itemType === 'service' || result.product.isMisc)) {
        showInfo('No aplica', 'Esto solo cambia precios de productos. Los servicios no.');
      }
    } catch {
      setBulkPriceSearch(barcodeSearchFieldValue({ barcode: '' }, rawCode));
    }
  }, []);

  const handleBulkPriceSearchHardwareSubmit = useCallback(() => {
    if (!barcodeAllowHardware) return;
    const q = bulkPriceSearch.trim();
    if (!looksLikeBarcodeHardwareSubmit(q)) return;
    if (!catalogBulkPriceDedupe(q)) return;
    void handleBulkPriceScan(q);
  }, [barcodeAllowHardware, bulkPriceSearch, catalogBulkPriceDedupe, handleBulkPriceScan]);

  // ── Product history ──
  const authUser = useAuthStore((state) => state.user);
  const employeesRaw = useEmployeesStore((state) => state.employees);
  const selfOwner = useEmployeesStore((state) => state.selfOwner);
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);

  const [productHistoryVisible, setProductHistoryVisible] = useState(false);
  const [productHistoryProduct, setProductHistoryProduct] = useState<CatalogProductRow | null>(null);
  const [productCatalogHistoryRows, setProductCatalogHistoryRows] = useState<ProductCatalogEventRow[]>([]);
  const [productHistoryLoading, setProductHistoryLoading] = useState(false);
  const [productHistoryLoadingMore, setProductHistoryLoadingMore] = useState(false);
  const [productHistoryHasMore, setProductHistoryHasMore] = useState(false);
  const [productHistoryOffset, setProductHistoryOffset] = useState(0);
  const [productHistoryEventFilter, setProductHistoryEventFilter] = useState('all');
  const [productHistoryFromDate, setProductHistoryFromDate] = useState('');
  const [productHistoryToDate, setProductHistoryToDate] = useState('');
  const [productHistoryCategories, setProductHistoryCategories] = useState<CategoryRecord[]>([]);

  const buildProductHistoryFilters = useCallback((): ProductCatalogHistoryFilters => {
    const f: ProductCatalogHistoryFilters = {};
    try {
      const from = productHistoryFromDate.trim();
      const to = productHistoryToDate.trim();
      if (from) {
        const d = new Date(`${from}T00:00:00`);
        if (!Number.isNaN(d.getTime())) f.fromCreatedAt = d.toISOString();
      }
      if (to) {
        const d = new Date(`${to}T23:59:59.999`);
        if (!Number.isNaN(d.getTime())) f.toCreatedAt = d.toISOString();
      }
      if (productHistoryEventFilter !== 'all') f.eventType = productHistoryEventFilter;
    } catch {
      // ponytail: fechas inválidas → sin filtro de rango
    }
    return f;
  }, [productHistoryFromDate, productHistoryToDate, productHistoryEventFilter]);

  const actorLabelByAuthId = useMemo(() => {
    const map: Record<string, string> = {};
    if (authUser?.id) map[authUser.id] = authUser.email ?? authUser.id;
    for (const e of employees) {
      if (e.authUserId) map[e.authUserId] = e.name;
    }
    return map;
  }, [authUser, employees]);

  const actorLabel = useCallback(
    (uid: string | null | undefined) => (uid ? actorLabelByAuthId[uid] ?? `${uid.slice(0, 8)}…` : '—'),
    [actorLabelByAuthId],
  );

  const formatEventSummary = useCallback(
    (row: ProductCatalogEventRow) => {
      const resolveCategory = createCategoryDisplayResolver(productHistoryCategories);
      return formatProductCatalogEventSummary(row, resolveCategory);
    },
    [productHistoryCategories],
  );

  const productHistoryEventOptions = useMemo(
    () => [
      { value: 'all', label: 'Todos' },
      { value: 'create', label: getProductCatalogEventTypeLabel('create') },
      { value: 'update', label: getProductCatalogEventTypeLabel('update') },
      { value: 'deactivate', label: getProductCatalogEventTypeLabel('deactivate') },
      { value: 'reactivate', label: getProductCatalogEventTypeLabel('reactivate') },
      { value: 'bulk_price', label: getProductCatalogEventTypeLabel('bulk_price') },
    ],
    [],
  );

  // El historial solo se abre desde el detalle: al cerrar, volvemos al detalle
  // del mismo producto en vez de caer al listado.
  const historyReturnProductRef = useRef<CatalogProductRow | null>(null);

  const openProductHistory = useCallback(async (product: CatalogProductRow) => {
    historyReturnProductRef.current = product;
    setProductHistoryProduct(product);
    setProductHistoryVisible(true);
    setProductHistoryLoading(true);
    setProductHistoryOffset(0);
    setProductHistoryHasMore(false);
    setProductHistoryLoadingMore(false);
    setProductHistoryEventFilter('all');
    setProductHistoryFromDate('');
    setProductHistoryToDate('');
    try {
      await useEmployeesStore.getState().loadEmployees().catch(() => {});
      const categories = await listCategories({ includeInactive: true });
      setProductHistoryCategories(categories);
      const rows = await getProductCatalogHistory(product.id, CATALOG_HISTORY_PAGE_SIZE, 0, {});
      setProductCatalogHistoryRows(rows);
      setProductHistoryOffset(rows.length);
      setProductHistoryHasMore(rows.length === CATALOG_HISTORY_PAGE_SIZE);
    } catch (error) {
      console.warn('[Catalog] No se pudo abrir historial', error);
      setProductCatalogHistoryRows([]);
      setProductHistoryOffset(0);
      setProductHistoryHasMore(false);
    } finally {
      setProductHistoryLoading(false);
    }
  }, []);

  const applyProductHistoryFilters = useCallback(async () => {
    if (!productHistoryProduct) return;
    setProductHistoryLoading(true);
    setProductHistoryOffset(0);
    setProductHistoryHasMore(false);
    setProductHistoryLoadingMore(false);
    try {
      const rows = await getProductCatalogHistory(
        productHistoryProduct.id, CATALOG_HISTORY_PAGE_SIZE, 0, buildProductHistoryFilters(),
      );
      setProductCatalogHistoryRows(rows);
      setProductHistoryOffset(rows.length);
      setProductHistoryHasMore(rows.length === CATALOG_HISTORY_PAGE_SIZE);
    } catch (error) {
      console.warn('[Catalog] No se pudo aplicar filtros de historial', error);
      setProductCatalogHistoryRows([]);
      setProductHistoryOffset(0);
      setProductHistoryHasMore(false);
    } finally {
      setProductHistoryLoading(false);
    }
  }, [productHistoryProduct, buildProductHistoryFilters]);

  const clearProductHistoryFilters = useCallback(async () => {
    if (!productHistoryProduct) return;
    setProductHistoryEventFilter('all');
    setProductHistoryFromDate('');
    setProductHistoryToDate('');
    setProductHistoryLoading(true);
    setProductHistoryOffset(0);
    setProductHistoryHasMore(false);
    setProductHistoryLoadingMore(false);
    try {
      const rows = await getProductCatalogHistory(
        productHistoryProduct.id, CATALOG_HISTORY_PAGE_SIZE, 0, {},
      );
      setProductCatalogHistoryRows(rows);
      setProductHistoryOffset(rows.length);
      setProductHistoryHasMore(rows.length === CATALOG_HISTORY_PAGE_SIZE);
    } catch (error) {
      console.warn('[Catalog] No se pudo limpiar filtros de historial', error);
      setProductCatalogHistoryRows([]);
      setProductHistoryOffset(0);
      setProductHistoryHasMore(false);
    } finally {
      setProductHistoryLoading(false);
    }
  }, [productHistoryProduct]);

  const loadMoreProductHistory = useCallback(async () => {
    if (!productHistoryProduct || productHistoryLoadingMore || !productHistoryHasMore) return;
    setProductHistoryLoadingMore(true);
    try {
      const rows = await getProductCatalogHistory(
        productHistoryProduct.id, CATALOG_HISTORY_PAGE_SIZE, productHistoryOffset, buildProductHistoryFilters(),
      );
      setProductCatalogHistoryRows((current) => [...current, ...rows]);
      setProductHistoryOffset((current) => current + rows.length);
      setProductHistoryHasMore(rows.length === CATALOG_HISTORY_PAGE_SIZE);
    } catch (error) {
      console.warn('[Catalog] No se pudo cargar más historial', error);
      setProductHistoryHasMore(false);
    } finally {
      setProductHistoryLoadingMore(false);
    }
  }, [productHistoryHasMore, productHistoryLoadingMore, productHistoryOffset, productHistoryProduct, buildProductHistoryFilters]);

  const exportProductCatalogHistoryCsv = useCallback(async () => {
    if (!productHistoryProduct) return;
    try {
      const rows = await getProductCatalogHistoryForExport(productHistoryProduct.id, buildProductHistoryFilters());
      if (rows.length === 0) {
        showInfo('Sin datos', 'No hay eventos para exportar con los filtros actuales.');
        return;
      }
      const csv = buildProductCatalogHistoryCsv({
        productName: productHistoryProduct.name,
        rows,
        actorLabel,
        summaryForRow: formatEventSummary,
      });
      const safeName = productHistoryProduct.name.replace(/[^\w\-]+/g, '_').slice(0, 40);
      await shareOrDownloadCsv(csv, `historial_catalogo_${safeName}.csv`);
    } catch (e) {
      console.warn('[Catalog] export CSV historial', e);
      showError('Exportar', 'No se pudo generar el archivo. Reintentá.');
    }
  }, [productHistoryProduct, buildProductHistoryFilters, actorLabel, formatEventSummary]);

  const closeProductHistory = useCallback(() => {
    setProductHistoryVisible(false);
    setProductHistoryProduct(null);
    setProductCatalogHistoryRows([]);
    setProductHistoryOffset(0);
    setProductHistoryHasMore(false);
    setProductHistoryEventFilter('all');
    setProductHistoryFromDate('');
    setProductHistoryToDate('');
    setProductHistoryCategories([]);
    const returnTo = historyReturnProductRef.current;
    historyReturnProductRef.current = null;
    if (returnTo) setSelectedProduct(returnTo);
  }, [setSelectedProduct]);

  return {
    // barcode
    handleSearchScan,
    handleFormScan,
    productMatchesCurrentFilters,
    barcodeAllowCamera,
    barcodeAllowHardware,
    handleSearchHardwareSubmit,
    handleFormBarcodeHardwareSubmit,
    autoFocusCatalogSearchOnWeb: false,
    // confirm flow
    productConfirmModalProps,
    handleDeactivate,
    handleRequestDuplicate,
    handleRequestReactivate,
    closeProductConfirm,
    confirmProductAction,
    // bulk price
    bulkPriceVisible,
    setBulkPriceVisible,
    bulkPriceCategoryScope,
    setBulkPriceCategoryScope,
    bulkPriceApplyTotal,
    bulkPriceSaveProgress,
    bulkPriceSearch,
    setBulkPriceSearch,
    bulkPricePercentInput,
    setBulkPricePercentInput,
    bulkPricePreview,
    setBulkPricePreview,
    setBulkPriceApplyTotal,
    bulkPricePreviewLoading,
    bulkPriceSaving,
    bulkPriceScannerVisible,
    setBulkPriceScannerVisible,
    bulkPriceScopeCounts,
    bulkPriceRounding,
    setBulkPriceRounding,
    openBulkPriceModal,
    loadBulkPricePreview,
    applyBulkPriceUpdate,
    handleBulkPriceScan,
    handleBulkPriceSearchHardwareSubmit,
    resetBulkPrice,
    // product history
    productHistoryVisible,
    productHistoryProduct,
    productCatalogHistoryRows,
    productHistoryLoading,
    productHistoryLoadingMore,
    productHistoryHasMore,
    productHistoryEventFilter,
    setProductHistoryEventFilter,
    productHistoryFromDate,
    setProductHistoryFromDate,
    productHistoryToDate,
    setProductHistoryToDate,
    openProductHistory,
    closeProductHistory,
    applyProductHistoryFilters,
    clearProductHistoryFilters,
    loadMoreProductHistory,
    exportProductCatalogHistoryCsv,
    actorLabel,
    formatEventSummary,
    productHistoryEventOptions,
  };
}
